"use client";

import { ChevronLeft, Search, X } from "lucide-react";
import { createContext, type ReactNode, useContext } from "react";
import { DrawerDescription, DrawerTitle } from "@/components/ui/drawer";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { CHAT_IG_THEME } from "./chat-theme";

type SegmentedControlOption<T extends string> = {
  value: T;
  label: string;
};

interface ChatSegmentedControlProps<T extends string> {
  value: T;
  options: SegmentedControlOption<T>[];
  onChange: (value: T) => void;
  className?: string;
  stretch?: boolean;
}

export function ChatSegmentedControl<T extends string>({
  value,
  options,
  onChange,
  className,
  stretch = false,
}: ChatSegmentedControlProps<T>) {
  return (
    <Tabs
      value={value}
      onValueChange={(nextValue) => onChange(nextValue as T)}
      className={cn(stretch && "w-full", className)}
    >
      <TabsList className={stretch ? "flex w-full" : "w-fit"}>
        {options.map((option) => (
          <TabsTrigger
            key={option.value}
            value={option.value}
            className={stretch ? "flex-1" : undefined}
          >
            {option.label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

interface ChatSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  leading?: ReactNode;
  autoFocus?: boolean;
}

export function ChatSearchInput({
  value,
  onChange,
  placeholder = "Search",
  className,
  leading,
  autoFocus = false,
}: ChatSearchInputProps) {
  return (
    <div className={cn("relative", className)}>
      <div
        className={cn(
          "pointer-events-none absolute top-1/2 left-3 -translate-y-1/2",
          CHAT_IG_THEME.mutedText,
        )}
      >
        {leading ?? <Search className="h-4 w-4" />}
      </div>
      <input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        // biome-ignore lint/a11y/noAutofocus: only when the viewer tapped a search affordance to get here.
        autoFocus={autoFocus}
        className={cn(
          "h-10 w-full rounded-xl border border-transparent pr-10 pl-9 text-base placeholder:text-text-tertiary focus:border-border-strong focus:outline-none md:text-[15px]",
          CHAT_IG_THEME.inputBg,
          CHAT_IG_THEME.titleText,
        )}
      />
      {value.trim().length > 0 ? (
        <button
          type="button"
          onClick={() => onChange("")}
          className={cn(
            "absolute top-1/2 right-2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full transition-colors",
            CHAT_IG_THEME.mutedText,
            "hover:bg-surface hover:text-text-primary",
          )}
          aria-label="Clear search"
        >
          <X className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
}

/**
 * Set by a sheet that shows sub-pages (chat details → members, search, …):
 * every `ChatDrawerHeader` inside one gets a back arrow that returns to the
 * sheet's main page. Closing is always the handle — drawers carry no X.
 */
export const ChatDrawerBackContext = createContext<(() => void) | null>(null);

interface ChatDrawerHeaderProps {
  title: string;
  subtitle?: string;
  /** Back within this sheet; defaults to the surrounding sheet's back. */
  onBack?: () => void;
  className?: string;
}

export function ChatDrawerHeader({
  title,
  subtitle,
  onBack,
  className,
}: ChatDrawerHeaderProps) {
  const contextBack = useContext(ChatDrawerBackContext);
  const back = onBack ?? contextBack;
  return (
    // Sits right under the drag handle. A bottom sheet already starts below
    // the status bar, so no top safe-area inset here.
    <div
      className={cn(
        "relative flex min-h-12 shrink-0 flex-col items-center justify-center px-14 pt-1 pb-2 text-center",
        className,
      )}
    >
      {back ? (
        <button
          type="button"
          onClick={back}
          aria-label="Back"
          className={cn(
            "absolute top-1/2 left-2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full transition-colors",
            CHAT_IG_THEME.titleText,
            CHAT_IG_THEME.hoverBg,
          )}
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
      ) : null}
      <DrawerTitle
        className={cn(
          "max-w-full truncate text-[16px] font-semibold",
          CHAT_IG_THEME.titleText,
        )}
      >
        {title}
      </DrawerTitle>
      {subtitle ? (
        <DrawerDescription
          className={cn(
            "mt-0.5 line-clamp-2 text-[12px]",
            CHAT_IG_THEME.mutedText,
          )}
        >
          {subtitle}
        </DrawerDescription>
      ) : null}
    </div>
  );
}

interface ChatTagProps {
  label: string;
  tone?: "default" | "muted" | "accent";
  className?: string;
}

export function ChatTag({ label, tone = "default", className }: ChatTagProps) {
  return (
    <span
      className={cn(
        "rounded-full px-1.5 py-0.5 text-[10px]",
        tone === "accent"
          ? `${CHAT_IG_THEME.accentBgSoft} ${CHAT_IG_THEME.accentText}`
          : tone === "muted"
            ? `${CHAT_IG_THEME.softSurface} ${CHAT_IG_THEME.mutedText}`
            : `${CHAT_IG_THEME.chipSurface} ${CHAT_IG_THEME.secondaryText}`,
        className,
      )}
    >
      {label}
    </span>
  );
}
